/**
 * MoneyGram Reference Data Service Implementation
 * Handles countries, regions, and other reference data
 */

import type { AxiosInstance } from "axios";
import { getActiveUserLanguageTag } from "@/utils/user-language";
import type {
	GetPayoutEntitiesRequest,
	ReferenceDataService,
} from "../contracts/reference-data.contract";
import type { RequestConfig } from "../types/common";
import type {
	Country,
	PayoutEntitiesResponse,
	Region,
} from "../types/moneygram";

export class MoneyGramReferenceDataService implements ReferenceDataService {
	constructor(private readonly httpClient: AxiosInstance) {}

	async getCountries(config?: RequestConfig): Promise<Country[]> {
		try {
			const response = await this.httpClient.get("/reference-data/countries", {
				signal: config?.signal,
				timeout: config?.timeout,
			});

			// Transform API response to our domain model
			return this.transformCountriesResponse(response.data);
		} catch (error) {
			throw this.handleApiError(error, "Failed to fetch countries");
		}
	}

	async getRegions(
		countryCode: string,
		config?: RequestConfig,
	): Promise<Region[]> {
		try {
			// Use the correct MoneyGram API endpoint with userLanguage parameter.
			// Reads from the active store locale so partner-supplied language
			// preferences round-trip correctly.
			const response = await this.httpClient.get(
				`/reference-data/countries/${countryCode}?userLanguage=${encodeURIComponent(getActiveUserLanguageTag())}`,
				{
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);

			return this.transformRegionsResponse(response.data, countryCode);
		} catch (error) {
			throw this.handleApiError(
				error,
				`Failed to fetch regions for country ${countryCode}`,
			);
		}
	}

	async getCountryByCode(
		countryCode: string,
		config?: RequestConfig,
	): Promise<Country | null> {
		try {
			const countries = await this.getCountries(config);
			return (
				countries.find((country) => country.iso2Code === countryCode) || null
			);
		} catch (error) {
			throw this.handleApiError(
				error,
				`Failed to fetch country ${countryCode}`,
			);
		}
	}

	async getPayoutEntities(
		request: GetPayoutEntitiesRequest,
		config?: RequestConfig,
	): Promise<PayoutEntitiesResponse> {
		try {
			const response = await this.httpClient.get(
				"reference-data/payout-entities",
				{
					params: {
						sendCountry: request.sendCountry,
						destinationCountry: request.destinationCountry,
						deliveryOptionId: request.deliveryOptionId,
						receiveCurrency: request.receiveCurrency,
						receiveAgentId: request.receiveAgentId,
						sendCurrencyCode: request.sendCurrencyCode,
						userLanguage: request.userLanguage || getActiveUserLanguageTag(),
					},
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);
			return response.data;
		} catch (error) {
			throw this.handleApiError(error, "Failed to fetch payout entities");
		}
	}

	/**
	 * Transform the API response to our domain model
	 */
	private transformCountriesResponse(apiData: unknown): Country[] {
		// Handle the actual API response format: { countries: [...] }
		const responseData = apiData as Record<string, unknown>;

		if (!responseData || typeof responseData !== "object") {
			// Unexpected countries API response format
			return [];
		}

		const countriesArray = responseData.countries;
		if (!Array.isArray(countriesArray)) {
			// Countries data is not an array
			return [];
		}

		return countriesArray.map((item: Record<string, unknown>) => ({
			iso3Code: String(item.iso3Code || ""),
			iso2Code: String(item.iso2Code || ""),
			name: String(item.name || ""),
			baseCurrency: item.baseCurrency ? String(item.baseCurrency) : undefined,
			sendActive: Boolean(item.sendActive),
			receiveActive: Boolean(item.receiveActive),
			indicativeRateActive: Boolean(item.indicativeRateActive),
			active: Boolean(item.active),
			sendCurrencies: Array.isArray(item.sendCurrencies)
				? item.sendCurrencies.map((c) => String(c))
				: [],
			receiveCurrencies: Array.isArray(item.receiveCurrencies)
				? item.receiveCurrencies.map((c) => String(c))
				: [],
			phoneDialCodes: Array.isArray(item.phoneDialCodes)
				? item.phoneDialCodes.map((c) => String(c))
				: [],
			subdivisions: Array.isArray(item.subdivisions)
				? item.subdivisions.map((sub: Record<string, unknown>) => ({
						code: String(sub.code || ""),
						name: String(sub.name || ""),
					}))
				: undefined,
		}));
	}

	/**
	 * Transform regions API response
	 * MoneyGram returns object with countries array containing one country with subdivisions
	 */
	private transformRegionsResponse(
		apiData: unknown,
		countryCode: string,
	): Region[] {
		// Handle the wrapped response format: { countries: [...] }
		const responseData = apiData as Record<string, unknown>;

		if (!responseData || typeof responseData !== "object") {
			// Unexpected regions API response format
			return [];
		}

		// Check if it's the wrapped format
		let countriesArray: unknown[];
		if (Array.isArray(responseData.countries)) {
			countriesArray = responseData.countries;
		} else if (Array.isArray(responseData)) {
			// Fallback for direct array format
			countriesArray = responseData as unknown[];
		} else {
			console.warn(
				"No countries array found in regions response:",
				responseData,
			);
			return [];
		}

		if (countriesArray.length === 0) {
			// Empty countries array in regions API response
			return [];
		}

		const countryData = countriesArray[0] as Record<string, unknown>;

		if (!countryData || typeof countryData !== "object") {
			// Unexpected country data in regions response
			return [];
		}

		if (!Array.isArray(countryData.subdivisions)) {
			// No subdivisions found in country data
			return [];
		}

		return countryData.subdivisions.map(
			(subdivision: Record<string, unknown>) => ({
				id: String(subdivision.code || ""),
				code: String(subdivision.code || ""), // e.g., "US-AK"
				name: String(subdivision.name || ""), // e.g., "Alaska"
				countryCode,
			}),
		);
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return new Error(`${defaultMessage}: ${error.message}`);
		}

		// Handle axios errors specifically
		if (typeof error === "object" && error && "response" in error) {
			const axiosError = error as {
				response: { data?: { message?: string }; status?: number };
			};
			const status = axiosError.response?.status;
			const message = axiosError.response?.data?.message || defaultMessage;
			return new Error(`${message} (Status: ${status})`);
		}

		// Handle network errors
		if (typeof error === "object" && error && "code" in error) {
			const networkError = error as { code: string };
			return new Error(
				`${defaultMessage}: Network error (${networkError.code})`,
			);
		}

		return new Error(defaultMessage);
	}
}
